/**
 * B-44.1 "Find a tour" — who sees it, and the filter logic for the listing.
 *
 * Pure functions only (no React; the one import, ./sport, is pure too) so the
 * backend's `node --test` can load it: backend/test/tourListing.test.js.
 * Dates are compared as 'YYYY-MM-DD' strings, so pass `today` in that form
 * (todayIso() gives the device's local date).
 */
import { isRugbyUnion } from './sport';

// ── Who sees it ────────────────────────────────────────────────
// Rugby union clubs only (lib/sport — shared with other rugby-only features).

// Club admins, team managers and coaches — the club role or any team hat
// (v17 teamRoles), mirroring ClubHub's hasTeamRole. Sponsors never.
const STAFF_ROLES = ['admin', 'team_manager', 'coach'];
export const isTourOrganiser = (membership) => {
  if (!membership || membership.role === 'sponsor') return false;
  if (membership.clubAdmin || STAFF_ROLES.includes(membership.role)) return true;
  return (membership.teamRoles || []).some(tr => tr && STAFF_ROLES.includes(tr.role));
};

export const canSeeTours = (sport, membership) => isRugbyUnion(sport) && isTourOrganiser(membership);

// ── Age bands ──────────────────────────────────────────────────
// The research calls U7–U12 "minis" (RFU age-grade). The page splits the
// older age grades in two: juniors U13–U16 (what most "mini & junior"
// festivals mean) and youth U17–U19 (colts).
export const AGE_BANDS = [
  { key: 'minis', label: 'Minis', range: 'U12 and under' },
  { key: 'juniors', label: 'Juniors', range: 'U13–U16' },
  { key: 'youth', label: 'Youth', range: 'U17–U19' },
  { key: 'adults', label: 'Adults', range: '18+' },
];

const bandForAge = (n) => (n <= 12 ? 'minis' : n <= 16 ? 'juniors' : 'youth');

/** The bands a tour's `ageGroups` cover, e.g. ["U12 girls","Adults (18+)"] → {minis, adults}. */
export function ageBandsFor(ageGroups) {
  const bands = new Set();
  for (const raw of ageGroups || []) {
    const g = String(raw);
    const u = g.match(/\bU(\d{1,2})\b/i);
    if (u) bands.add(bandForAge(Number(u[1])));
    else if (/\bminis?\b/i.test(g)) bands.add('minis');
    else if (/\byouth\b/i.test(g)) { bands.add('juniors'); bands.add('youth'); }
    if (/adult|veteran|\bvets\b|18\+|over-\d+/i.test(g)) bands.add('adults');
  }
  return bands;
}

/** "U7","U8",…,"U12","U12 girls" → "U7–U12, U12 girls" (runs of plain U-ages collapse). */
export function ageGroupsLabel(ageGroups) {
  const out = [];
  let run = null; // { from, to }
  const flush = () => {
    if (run) out.push(run.from === run.to ? `U${run.from}` : `U${run.from}–U${run.to}`);
    run = null;
  };
  for (const raw of ageGroups || []) {
    const m = String(raw).match(/^U(\d{1,2})$/);
    if (m) {
      const n = Number(m[1]);
      if (run && n === run.to + 1) run.to = n;
      else { flush(); run = { from: n, to: n }; }
    } else {
      flush();
      out.push(String(raw));
    }
  }
  flush();
  return out.join(', ');
}

// ── Where ──────────────────────────────────────────────────────
const UK_COUNTRIES = ['england', 'wales', 'scotland', 'northern ireland'];
export const isUk = (country) => UK_COUNTRIES.includes(String(country || '').trim().toLowerCase());

// ── When ───────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n) => String(n).padStart(2, '0');

/** The device's local date as 'YYYY-MM-DD'. */
export const todayIso = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** 'YYYY-MM' months a tour touches: start → end, or its usual months when dates are TBC. */
export function monthsFor(tour) {
  if (tour.startDate && tour.endDate) {
    const months = [];
    let [y, m] = tour.startDate.slice(0, 7).split('-').map(Number);
    const end = tour.endDate.slice(0, 7);
    for (let i = 0; i < 24; i += 1) {
      const key = `${y}-${pad2(m)}`;
      months.push(key);
      if (key >= end) break;
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return months;
  }
  return Array.isArray(tour.usualMonths) ? [...tour.usualMonths] : [];
}

/** 'YYYY-MM' → 'Apr 2027'. */
export const monthLabel = (key) => {
  const [y, m] = String(key).split('-').map(Number);
  return MONTHS[m - 1] ? `${MONTHS[m - 1]} ${y}` : String(key);
};

/** 'YYYY-MM-DD' → '14 Oct 2026'. */
export const dateLabel = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return MONTHS[m - 1] && d ? `${d} ${MONTHS[m - 1]} ${y}` : String(iso || '');
};

/**
 * Whether the card should say "dates TBC" (unconfirmed, and the text doesn't
 * say so already). WT-26: not on flexible-date tours (`datesFlexible`, e.g. a
 * tour company's "you choose the dates"): nothing is waiting to be confirmed
 * there, and the badge contradicted the card. An explicit flag (QA), so a
 * future row that simply has no months yet still gets its badge.
 */
export const needsTbcLabel = (tour) => !tour.datesConfirmed && !tour.datesFlexible
  && !/\btbc\b/i.test(tour.dates || '');

// ── The listing ────────────────────────────────────────────────
/**
 * On the page at all: status 'open' and not finished. Closed/recheck rows
 * never show. "Finished" is endDate passed; a TBC row (no endDate) is
 * finished once the last of its usual months is behind us. Flexible rows
 * (no dates, no usual months) stay until someone edits them.
 */
export function isListed(tour, today) {
  if (tour.status !== 'open') return false;
  if (tour.endDate) return tour.endDate >= today;
  const usual = Array.isArray(tour.usualMonths) ? tour.usualMonths : [];
  if (usual.length) return [...usual].sort()[usual.length - 1] >= String(today).slice(0, 7);
  return true;
}

const sortKey = (t) => t.startDate || (t.usualMonths && t.usualMonths[0] ? `${t.usualMonths[0]}-32` : '9999');

/** Listed tours, soonest first; TBC rows sit after the dated ones in their usual month, flexible ones last. */
export const listedTours = (tours, today) =>
  (tours || [])
    .filter(t => isListed(t, today))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)) || a.name.localeCompare(b.name));

/**
 * Filter the listing. `band` is an AGE_BANDS key, `region` 'uk' | 'overseas',
 * `month` 'YYYY-MM'; empty/absent means "any".
 *
 * Returns { tours, listedCount, undatedHidden } — undatedHidden counts tours
 * that matched everything except the month because they have no dates yet
 * (flexible), so the page can say why they're missing rather than drop them
 * silently.
 */
export function filterTours(tours, { band = '', region = '', month = '' } = {}, today) {
  const listed = listedTours(tours, today);
  let undatedHidden = 0;
  const out = listed.filter((t) => {
    if (band && !ageBandsFor(t.ageGroups).has(band)) return false;
    if (region === 'uk' && !isUk(t.country)) return false;
    if (region === 'overseas' && isUk(t.country)) return false;
    if (month) {
      const months = monthsFor(t);
      if (!months.length) { undatedHidden += 1; return false; }
      if (!months.includes(month)) return false;
    }
    return true;
  });
  return { tours: out, listedCount: listed.length, undatedHidden };
}

/** Month choices for the filter: every month from this one on that a listed tour touches, in order. */
export const monthOptions = (tours, today) => {
  const thisMonth = String(today).slice(0, 7);
  return [...new Set(listedTours(tours, today).flatMap(monthsFor))].filter(m => m >= thisMonth).sort();
};
